import { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { saveDateRangePreset, getDateRangePreset, saveCustomDateRange, getCustomDateRange } from '../utils/localStorage';

// Date range preset values (not labels)
export const DATE_RANGE_PRESET_VALUES = {
  TWO_WEEKS: '2w',
  ONE_MONTH: '1m',
  TWO_MONTHS: '2m',
  THREE_MONTHS: '3m',
  SIX_MONTHS: '6m',
  ONE_YEAR: '1y',
  CUSTOM: 'custom',
  ALL: 'all'
};

interface UseDateRangeProps {
  parsedDates: Date[];
}

interface DateRangeResult {
  dateRangePreset: string;
  setDateRangePreset: (value: string) => void;
  customDateRange: [Date | null, Date | null];
  setCustomDateRange: (range: [Date | null, Date | null]) => void;
  showCustomDateModal: boolean;
  setShowCustomDateModal: (show: boolean) => void;
  dateRange: {
    startDate: Date | null;
    endDate: Date | null;
  };
  handleDateRangeChange: (value: string | null) => void;
  handleCustomDateChange: (range: [Date | null, Date | null]) => void;
  dateRangePresets: { value: string; label: string }[];
}

export const useDateRange = ({ parsedDates }: UseDateRangeProps): DateRangeResult => {
  const { t } = useTranslation();
  // Initialize from localStorage if available
  const initialPreset = getDateRangePreset() || DATE_RANGE_PRESET_VALUES.ONE_MONTH;
  const initialCustomRange = getCustomDateRange();
  const [dateRangePreset, setDateRangePresetState] = useState<string>(initialPreset);
  const [customDateRange, setCustomDateRangeState] = useState<[Date | null, Date | null]>(initialCustomRange);
  const [showCustomDateModal, setShowCustomDateModal] = useState(false);
  
  // Generate date range presets with translated labels
  const dateRangePresets = useMemo(() => [
    { value: DATE_RANGE_PRESET_VALUES.TWO_WEEKS, label: t('dateRange.last2weeks') },
    { value: DATE_RANGE_PRESET_VALUES.ONE_MONTH, label: t('dateRange.lastMonth') },
    { value: DATE_RANGE_PRESET_VALUES.THREE_MONTHS, label: t('dateRange.last3months') },
    { value: DATE_RANGE_PRESET_VALUES.ONE_YEAR, label: t('dateRange.lastYear') },
    { value: DATE_RANGE_PRESET_VALUES.CUSTOM, label: t('dateRange.customRange') },
    { value: DATE_RANGE_PRESET_VALUES.ALL, label: t('dateRange.allData') },
  ], [t]);

  // Calculate the date range to use based on preset or custom selection
  const dateRange = useMemo(() => {
    let startDate: Date | null = null;
    let endDate: Date | null = null;
    const now = new Date();
    
    if (dateRangePreset === DATE_RANGE_PRESET_VALUES.ALL) {
      // Use the full date range from the data
      const sortedDates = [...parsedDates].sort((a, b) => a.getTime() - b.getTime());
      if (sortedDates.length > 0) {
        startDate = sortedDates[0];
        endDate = sortedDates[sortedDates.length - 1];
      }
    } else if (dateRangePreset === DATE_RANGE_PRESET_VALUES.CUSTOM && customDateRange[0] && customDateRange[1]) {
      startDate = customDateRange[0];
      endDate = customDateRange[1];
    } else {
      // Handle preset ranges
      endDate = now;
      
      // Initialize sortedDates for default case
      let sortedDates: Date[] = [];
      
      switch (dateRangePreset) {
        case DATE_RANGE_PRESET_VALUES.TWO_WEEKS:
          startDate = new Date(now);
          startDate.setDate(now.getDate() - 14);
          break;
        case DATE_RANGE_PRESET_VALUES.ONE_MONTH:
          startDate = new Date(now);
          startDate.setMonth(now.getMonth() - 1);
          break;
        case DATE_RANGE_PRESET_VALUES.TWO_MONTHS:
          startDate = new Date(now);
          startDate.setMonth(now.getMonth() - 2);
          break;
        case DATE_RANGE_PRESET_VALUES.THREE_MONTHS:
          startDate = new Date(now);
          startDate.setMonth(now.getMonth() - 3);
          break;
        case DATE_RANGE_PRESET_VALUES.SIX_MONTHS:
          startDate = new Date(now);
          startDate.setMonth(now.getMonth() - 6);
          break;
        case DATE_RANGE_PRESET_VALUES.ONE_YEAR:
          startDate = new Date(now);
          startDate.setFullYear(now.getFullYear() - 1);
          break;
        default:
          // Default to full range if preset is not recognized
          sortedDates = [...parsedDates].sort((a, b) => a.getTime() - b.getTime());
          if (sortedDates.length > 0) {
            startDate = sortedDates[0];
            endDate = sortedDates[sortedDates.length - 1];
          }
      }
    }
    
    return { startDate, endDate };
  }, [dateRangePreset, customDateRange, parsedDates]);

  // Persist dateRangePreset to localStorage
  const setDateRangePreset = (value: string) => {
    setDateRangePresetState(value);
    saveDateRangePreset(value);
  };

  // Persist customDateRange to localStorage
  const setCustomDateRange = (range: [Date | null, Date | null]) => {
    setCustomDateRangeState(range);
    saveCustomDateRange(range);
  };

  const handleDateRangeChange = (value: string | null) => {
    if (value) {
      setDateRangePreset(value);
      if (value === DATE_RANGE_PRESET_VALUES.CUSTOM) 
        setShowCustomDateModal(true);
      
    }
  };

  const handleCustomDateChange = (range: [Date | null, Date | null]) => {
    setCustomDateRange(range);
    setShowCustomDateModal(false);
  };

  return {
    dateRangePreset,
    setDateRangePreset,
    customDateRange,
    setCustomDateRange,
    showCustomDateModal,
    setShowCustomDateModal,
    dateRange,
    handleDateRangeChange,
    handleCustomDateChange,
    dateRangePresets
  };
}; 